import type { Role } from "@/lib/types";

// Source unique des libellés de rôle et de leur ordre d'affichage : sélecteurs
// d'invitation, changement de rôle, matrice « Rôles & permissions », instruction
// des demandes d'inscription. Ne jamais redéclarer ces libellés ailleurs.

/**
 * Ordre d'affichage (hiérarchique, du moins au plus étendu). Il est volontairement
 * différent de l'ordre de l'enum côté API : celui-ci est figé par la base.
 */
export const ROLE_ORDER: readonly Role[] = [
  "etudiant",
  "stagiaire",
  "rayonniste",
  "preparateur",
  "collaborateur",
  "adjoint",
  "titulaire",
];

/** Libellé court : chips, pastilles, lignes d'équipe. */
export const ROLE_LABELS: Record<Role, string> = {
  etudiant: "Étudiant",
  stagiaire: "Stagiaire",
  rayonniste: "Rayonniste",
  preparateur: "Préparateur",
  collaborateur: "Collaborateur",
  adjoint: "Adjoint",
  titulaire: "Titulaire",
};

/**
 * Libellé long/formel, employé là où le rôle est le sujet principal de l'écran
 * (formulaire d'inscription, instruction d'une demande côté plateforme).
 */
export const ROLE_LONG_LABELS: Record<Role, string> = {
  etudiant: "Étudiant",
  stagiaire: "Stagiaire",
  rayonniste: "Rayonniste",
  preparateur: "Préparateur / Préparatrice",
  collaborateur: "Collaborateur",
  adjoint: "Pharmacien adjoint",
  titulaire: "Pharmacien titulaire",
};

/** Texte d'aide affiché sous le libellé dans les sélecteurs de rôle. */
export const ROLE_HELP: Record<Role, string> = {
  etudiant: "Consultation seule : accès aux modules, aucune gestion.",
  stagiaire: "Consultation seule : accès aux modules, aucune gestion.",
  rayonniste: "Gère les ruptures et les commandes, sans ordonnances ni locations.",
  preparateur: "Accès standard aux modules du quotidien.",
  collaborateur: "Mêmes droits qu'un pharmacien adjoint.",
  adjoint: "Peut publier des actualités et valider.",
  titulaire: "Accès complet, y compris l'administration.",
};

/** Options prêtes à l'emploi pour les sélecteurs de rôle, dans l'ordre d'affichage. */
export const ROLE_OPTIONS: { value: Role; label: string; help: string }[] = ROLE_ORDER.map(
  (value) => ({ value, label: ROLE_LABELS[value], help: ROLE_HELP[value] }),
);

/**
 * Libellé court tolérant : si l'API renvoie un rôle que ce front ne connaît pas
 * encore, on affiche la valeur brute plutôt qu'une case vide.
 */
export function roleLabel(role: string | null | undefined): string {
  if (!role) return "Rôle inconnu";
  return ROLE_LABELS[role as Role] ?? role;
}
